import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CONNECTION_OPTIONS,
  DEPARTMENT_OPTIONS,
  IP_OPTIONS,
  PROJECT_SUMMARY_SUBTYPE_OPTIONS,
  PROJECT_SUMMARY_TYPE_OPTIONS,
  SERVICE_SPEED_OPTIONS,
} from "./commercialProjectSchema";
import { listMatrixUserEmailsByRoleName } from "./commercialRoleAccess";
import {
  canManageCrm,
  canViewCrm,
  opportunityStatusClass,
  opportunityStatusLabel,
  personRoleLabel,
  PERSON_ROLE_OPTIONS,
} from "./crmAccess";
import {
  closeCrmOpportunity,
  createCrmCompany,
  createCrmOpportunity,
  createCrmPerson,
  deleteCrmCompany,
  deleteCrmOpportunity,
  deleteCrmPerson,
  fetchCrmCompanies,
  fetchCrmCompany,
  fetchCrmOpportunities,
  fetchCrmOpportunity,
  fetchCrmPeople,
  fetchCrmPerson,
  syncCrmCompaniesFromProjects,
  updateCrmCompany,
  updateCrmOpportunity,
  updateCrmPerson,
} from "./crmApi";
import CrmCsvImportPanel from "./CrmCsvImportPanel";
import {
  emptyCrmLocation,
  emptyOpportunityForm,
  formToClosedOpportunityPayload,
  formToOpportunityPayload,
  locationHasDia,
  locationHasPtpOrDark,
  opportunityToForm,
  validateOpportunityForClose,
} from "./crmOpportunity";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import "./addProjectPage.css";
import "./crmPage.css";

const CRM_TABS = [
  { id: "opportunities", label: "Opportunities" },
  { id: "companies", label: "Companies" },
  { id: "people", label: "People" },
];

function FormField({ label, children, hint }) {
  return (
    <div className="add-project-field">
      <label>{label}</label>
      {children}
      {hint ? (
        <span className="project-detail-muted" style={{ fontSize: 12 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function CheckboxMultiSelect({ label, options, value, onChange, disabled, required }) {
  const selected = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
  return (
    <FormField label={label}>
      <div className="add-project-departments commercial-multiselect">
        {options.map((opt) => (
          <label key={opt} className="commercial-multiselect-option">
            <input
              type="checkbox"
              checked={selected.has(opt)}
              disabled={disabled}
              required={required && selected.size === 0}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                onChange([...next]);
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    </FormField>
  );
}

function matchesQuery(hay, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return String(hay || "")
    .toLowerCase()
    .includes(q);
}

function formatOpportunityWorkOrders(row) {
  const list = [];
  if (Array.isArray(row?.workOrders) && row.workOrders.length) {
    list.push(...row.workOrders.filter(Boolean));
  } else if (Array.isArray(row?.projects) && row.projects.length) {
    list.push(...row.projects.map((p) => p.workOrder).filter(Boolean));
  } else if (row?.workOrder) {
    list.push(row.workOrder);
  }
  return [...new Set(list)];
}

function WorkOrderLinksPicker({ projects, selectedIds, onChange, disabled }) {
  const [query, setQuery] = useState("");
  const selected = useMemo(
    () => new Set((selectedIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))),
    [selectedIds]
  );

  const allProjects = useMemo(() => {
    const byId = new Map();
    for (const row of projects || []) {
      if (row?.id == null) continue;
      byId.set(Number(row.id), {
        id: row.id,
        work_order: row.work_order || row.workOrder || "",
        name: row.name || "",
        customer: row.customer || "",
      });
    }
    return [...byId.values()];
  }, [projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...allProjects].sort((a, b) =>
      String(a.work_order || "").localeCompare(String(b.work_order || ""), undefined, {
        numeric: true,
      })
    );
    const match = (row) => {
      if (!q) return true;
      const hay = `${row.work_order || ""} ${row.name || ""} ${row.customer || ""}`.toLowerCase();
      return hay.includes(q);
    };
    const selectedRows = list.filter((row) => selected.has(Number(row.id)));
    const missingSelected = [...selected]
      .filter((id) => !list.some((row) => Number(row.id) === id))
      .map((id) => ({ id, work_order: `Project #${id}`, name: "", customer: "" }));
    const others = list.filter((row) => !selected.has(Number(row.id)) && match(row)).slice(0, 120);
    return [...missingSelected, ...selectedRows, ...others];
  }, [allProjects, query, selected]);

  const toggle = (id) => {
    if (disabled) return;
    const next = new Set(selected);
    const n = Number(id);
    if (next.has(n)) next.delete(n);
    else next.add(n);
    onChange([...next]);
  };

  return (
    <div className="crm-wo-picker">
      <input
        type="search"
        className="crm-search"
        style={{ width: "100%", marginBottom: 8 }}
        placeholder="Search commercial work orders…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
      />
      {selected.size ? (
        <p className="crm-muted" style={{ margin: "0 0 8px", fontSize: 13 }}>
          {selected.size} linked — first selected becomes the primary work order.
        </p>
      ) : (
        <p className="crm-muted" style={{ margin: "0 0 8px", fontSize: 13 }}>
          Optional. Link existing work orders for imported or multi-site deals.
        </p>
      )}
      <div className="crm-wo-picker-list">
        {filtered.length === 0 ? (
          <p className="crm-muted" style={{ margin: 0 }}>
            No work orders match.
          </p>
        ) : (
          filtered.map((row) => (
            <label key={row.id} className="crm-wo-picker-row">
              <input
                type="checkbox"
                checked={selected.has(Number(row.id))}
                disabled={disabled}
                onChange={() => toggle(row.id)}
              />
              <span>
                <strong>{row.work_order || `Project #${row.id}`}</strong>
                {row.name ? ` — ${row.name}` : ""}
                {row.customer ? (
                  <span className="crm-muted"> · {row.customer}</span>
                ) : null}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

function CrmShell({ children }) {
  return (
    <div className="crm-shell">
      <div className="crm-page">{children}</div>
    </div>
  );
}

function CompanyEditor({ company, saving, error, onSave, onDelete, onCancel }) {
  const isNew = !company?.id;
  const [form, setForm] = useState({
    name: company?.name || "",
    notes: company?.notes || "",
    accountingContactName: company?.accountingContactName || "",
    accountingContactEmail: company?.accountingContactEmail || "",
    accountingContactPhone: company?.accountingContactPhone || "",
  });

  useEffect(() => {
    setForm({
      name: company?.name || "",
      notes: company?.notes || "",
      accountingContactName: company?.accountingContactName || "",
      accountingContactEmail: company?.accountingContactEmail || "",
      accountingContactPhone: company?.accountingContactPhone || "",
    });
  }, [company]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <form
      className="add-project-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <section className="add-project-section">
        <h3 className="add-project-section-title">{isNew ? "New company" : "Company"}</h3>
        <FormField label="Company name">
          <input
            value={form.name}
            required
            disabled={saving}
            onChange={(e) => setField("name", e.target.value)}
          />
        </FormField>
        <FormField label="Notes">
          <textarea
            rows={3}
            value={form.notes}
            disabled={saving}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </FormField>
      </section>
      <section className="add-project-section">
        <h3 className="add-project-section-title">Accounting contact</h3>
        <p className="project-detail-muted" style={{ margin: 0, fontSize: 13 }}>
          Inherited by every opportunity for this company.
        </p>
        <FormField label="Contact name">
          <input
            value={form.accountingContactName}
            disabled={saving}
            onChange={(e) => setField("accountingContactName", e.target.value)}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="Email">
            <input
              type="email"
              value={form.accountingContactEmail}
              disabled={saving}
              onChange={(e) => setField("accountingContactEmail", e.target.value)}
            />
          </FormField>
          <FormField label="Phone">
            <input
              value={form.accountingContactPhone}
              disabled={saving}
              onChange={(e) => setField("accountingContactPhone", e.target.value)}
            />
          </FormField>
        </div>
      </section>
      {error ? <p className="crm-error">{error}</p> : null}
      <div className="crm-editor-actions">
        <button type="submit" className="crm-btn crm-btn--primary" disabled={saving}>
          {saving ? "Saving…" : "Save company"}
        </button>
        <button type="button" className="crm-btn" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        {!isNew ? (
          <button
            type="button"
            className="crm-btn crm-btn--danger"
            disabled={saving}
            onClick={onDelete}
          >
            Delete
          </button>
        ) : null}
      </div>
    </form>
  );
}

function PersonEditor({ person, companies, initialCompanyId, saving, error, onSave, onDelete, onCancel }) {
  const isNew = !person?.id;
  const [form, setForm] = useState({
    name: person?.name || "",
    companyId: person?.companyId || initialCompanyId || "",
    email: person?.email || "",
    phone: person?.phone || "",
    title: person?.title || "",
    role: person?.role || "other",
    notes: person?.notes || "",
  });

  useEffect(() => {
    setForm({
      name: person?.name || "",
      companyId: person?.companyId || initialCompanyId || "",
      email: person?.email || "",
      phone: person?.phone || "",
      title: person?.title || "",
      role: person?.role || "other",
      notes: person?.notes || "",
    });
  }, [person, initialCompanyId]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <form
      className="add-project-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          ...form,
          companyId: form.companyId ? Number(form.companyId) : null,
        });
      }}
    >
      <section className="add-project-section">
        <h3 className="add-project-section-title">{isNew ? "New person" : "Person"}</h3>
        <FormField label="Name">
          <input
            value={form.name}
            required
            disabled={saving}
            onChange={(e) => setField("name", e.target.value)}
          />
        </FormField>
        <FormField label="Company">
          <select
            value={form.companyId || ""}
            disabled={saving}
            onChange={(e) => setField("companyId", e.target.value)}
          >
            <option value="">— None —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Role">
          <select
            value={form.role}
            disabled={saving}
            onChange={(e) => setField("role", e.target.value)}
          >
            {PERSON_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Title">
          <input value={form.title} disabled={saving} onChange={(e) => setField("title", e.target.value)} />
        </FormField>
        <div className="add-project-row">
          <FormField label="Email">
            <input
              type="email"
              value={form.email}
              disabled={saving}
              onChange={(e) => setField("email", e.target.value)}
            />
          </FormField>
          <FormField label="Phone">
            <input value={form.phone} disabled={saving} onChange={(e) => setField("phone", e.target.value)} />
          </FormField>
        </div>
        <FormField label="Notes">
          <textarea
            rows={3}
            value={form.notes}
            disabled={saving}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </FormField>
      </section>
      {error ? <p className="crm-error">{error}</p> : null}
      <div className="crm-editor-actions">
        <button type="submit" className="crm-btn crm-btn--primary" disabled={saving}>
          {saving ? "Saving…" : "Save person"}
        </button>
        <button type="button" className="crm-btn" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        {!isNew ? (
          <button type="button" className="crm-btn crm-btn--danger" disabled={saving} onClick={onDelete}>
            Delete
          </button>
        ) : null}
      </div>
    </form>
  );
}

function applyPersonToContact(person, prefix) {
  if (!person) return {};
  if (prefix === "site") {
    return {
      siteContactPersonId: person.id,
      siteContactName: person.name || "",
      siteContactEmail: person.email || "",
      siteContactPhone: person.phone || "",
    };
  }
  return {
    itContactPersonId: person.id,
    itContactName: person.name || "",
    itContactEmail: person.email || "",
    itContactPhone: person.phone || "",
  };
}

function OpportunityEditor({
  opportunity,
  companies,
  people,
  commercialProjects,
  salesRepOptions,
  initialCompanyId,
  saving,
  error,
  onSave,
  onDelete,
  onCancel,
  onCloseSale,
  onMarkLost,
}) {
  const isNew = !opportunity?.id;
  const locked = opportunity?.status === "closed";
  const [form, setForm] = useState(() => {
    const next = opportunityToForm(opportunity);
    if (!opportunity?.id && initialCompanyId) next.companyId = String(initialCompanyId);
    return next;
  });

  useEffect(() => {
    const next = opportunityToForm(opportunity);
    if (!opportunity?.id && initialCompanyId) next.companyId = String(initialCompanyId);
    setForm(next);
  }, [opportunity, initialCompanyId]);

  const company = companies.find((c) => String(c.id) === String(form.companyId));
  const companyPeople = people.filter((p) => String(p.companyId) === String(form.companyId));
  const isRevenue = form.projectType === "Revenue";
  const isMultisite = form.locations.length > 1;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateLocation = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (i === index ? { ...loc, ...patch } : loc)),
    }));
  };

  const pickPerson = (personId, prefix) => {
    const person = companyPeople.find((p) => String(p.id) === String(personId));
    setForm((prev) => ({ ...prev, ...applyPersonToContact(person, prefix) }));
  };

  return (
    <form
      className="add-project-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <section className="add-project-section">
        <h3 className="add-project-section-title">{isNew ? "New opportunity" : "Opportunity"}</h3>
        {!isNew ? (
          <p className="crm-muted" style={{ margin: 0 }}>
            Status:{" "}
            <span className={`crm-status ${opportunityStatusClass(opportunity.status)}`}>
              {opportunityStatusLabel(opportunity.status)}
            </span>
            {formatOpportunityWorkOrders(opportunity).length ? (
              <>
                {" "}
                · Work order
                {formatOpportunityWorkOrders(opportunity).length > 1 ? "s" : ""}{" "}
                {formatOpportunityWorkOrders(opportunity).map((wo, i) => {
                  const project =
                    (opportunity.projects || []).find((p) => p.workOrder === wo) ||
                    (opportunity.projectId && opportunity.workOrder === wo
                      ? { id: opportunity.projectId }
                      : null);
                  return (
                    <React.Fragment key={wo}>
                      {i > 0 ? ", " : null}
                      {project?.id ? (
                        <Link to={`/project/${project.id}`}>{wo}</Link>
                      ) : (
                        <Link to={`/work-order/${wo}`}>{wo}</Link>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            ) : null}
          </p>
        ) : null}
        <FormField label="Company">
          <select
            value={form.companyId}
            required
            disabled={saving || locked}
            onChange={(e) => setField("companyId", e.target.value)}
          >
            <option value="">— Select company —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Title" hint="Defaults to the company name if left blank.">
          <input
            value={form.title}
            disabled={saving || locked}
            onChange={(e) => setField("title", e.target.value)}
          />
        </FormField>
        <FormField label="Work order type">
          <select
            value={form.projectType}
            disabled={saving || locked}
            onChange={(e) => setField("projectType", e.target.value)}
          >
            {PROJECT_SUMMARY_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </FormField>
        {isRevenue ? (
          <>
            <FormField label="New customer">
              <label className="commercial-multiselect-option" style={{ display: "inline-flex", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.newCustomer)}
                  disabled={saving || locked}
                  onChange={(e) => setField("newCustomer", e.target.checked)}
                />
                This is a new customer
              </label>
            </FormField>
            <div className="add-project-row">
              <FormField label="Term (months)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.termInMonths}
                  disabled={saving || locked}
                  onChange={(e) => setField("termInMonths", e.target.value)}
                />
              </FormField>
              <FormField label="Sales rep">
                <select
                  value={form.salesRep}
                  disabled={saving || locked}
                  onChange={(e) => setField("salesRep", e.target.value)}
                >
                  <option value="">— Select sales rep —</option>
                  {salesRepOptions.map((rep) => (
                    <option key={rep} value={rep}>
                      {rep}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </>
        ) : null}
        <FormField label="Notes">
          <textarea
            rows={3}
            value={form.notes}
            disabled={saving}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </FormField>
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">Accounting contact</h3>
        <div className="crm-inherit">
          {company?.accountingContactName || company?.accountingContactEmail || company?.accountingContactPhone
            ? [company.accountingContactName, company.accountingContactEmail, company.accountingContactPhone]
                .filter(Boolean)
                .join(" · ")
            : "Inherited from the company. Add an accounting contact on the company record."}
          {company?.id ? (
            <>
              {" "}
              <Link to={`/crm/companies/${company.id}`}>Edit company</Link>
            </>
          ) : null}
        </div>
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">Site contact</h3>
        {companyPeople.length ? (
          <FormField label="Choose from company people">
            <select
              value={form.siteContactPersonId || ""}
              disabled={saving || locked}
              onChange={(e) => pickPerson(e.target.value, "site")}
            >
              <option value="">— Type below or pick —</option>
              {companyPeople.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.role ? ` (${personRoleLabel(p.role)})` : ""}
                </option>
              ))}
            </select>
          </FormField>
        ) : null}
        <FormField label="Name">
          <input
            value={form.siteContactName}
            disabled={saving || locked}
            onChange={(e) => setField("siteContactName", e.target.value)}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="Email">
            <input
              type="email"
              value={form.siteContactEmail}
              disabled={saving || locked}
              onChange={(e) => setField("siteContactEmail", e.target.value)}
            />
          </FormField>
          <FormField label="Phone">
            <input
              value={form.siteContactPhone}
              disabled={saving || locked}
              onChange={(e) => setField("siteContactPhone", e.target.value)}
            />
          </FormField>
        </div>
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">IT contact</h3>
        {companyPeople.length ? (
          <FormField label="Choose from company people">
            <select
              value={form.itContactPersonId || ""}
              disabled={saving || locked}
              onChange={(e) => pickPerson(e.target.value, "it")}
            >
              <option value="">— Type below or pick —</option>
              {companyPeople.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.role ? ` (${personRoleLabel(p.role)})` : ""}
                </option>
              ))}
            </select>
          </FormField>
        ) : null}
        <FormField label="Name">
          <input
            value={form.itContactName}
            disabled={saving || locked}
            onChange={(e) => setField("itContactName", e.target.value)}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="Email">
            <input
              type="email"
              value={form.itContactEmail}
              disabled={saving || locked}
              onChange={(e) => setField("itContactEmail", e.target.value)}
            />
          </FormField>
          <FormField label="Phone">
            <input
              value={form.itContactPhone}
              disabled={saving || locked}
              onChange={(e) => setField("itContactPhone", e.target.value)}
            />
          </FormField>
        </div>
      </section>

      <section className="add-project-section">
        <div className="add-project-section-header">
          <h3 className="add-project-section-title">Service locations</h3>
          <button
            type="button"
            className="add-project-add-location"
            disabled={saving || locked}
            onClick={() =>
              setForm((prev) => ({ ...prev, locations: [...prev.locations, emptyCrmLocation()] }))
            }
          >
            + Add location
          </button>
        </div>
        {isMultisite ? (
          <p className="project-detail-muted" style={{ margin: 0, fontSize: 13 }}>
            Each location becomes its own sub-work order when this opportunity is Close Won.
          </p>
        ) : null}
        {form.locations.map((loc, index) => (
          <div key={index} className="add-project-location-card">
            {form.locations.length > 1 ? (
              <div className="add-project-location-header">
                <strong>Location {index + 1}</strong>
                <button
                  type="button"
                  className="add-project-remove-location"
                  disabled={saving || locked}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      locations: prev.locations.filter((_, i) => i !== index),
                    }))
                  }
                >
                  Remove
                </button>
              </div>
            ) : null}
            <CheckboxMultiSelect
              label="Departments involved"
              options={DEPARTMENT_OPTIONS}
              value={loc.departments}
              disabled={saving || locked}
              required
              onChange={(deps) => updateLocation(index, { departments: deps })}
            />
            <FormField label="Service location">
              <textarea
                rows={2}
                value={loc.serviceLocation}
                required={isRevenue}
                disabled={saving || locked}
                onChange={(e) => updateLocation(index, { serviceLocation: e.target.value })}
              />
            </FormField>
            {isRevenue ? (
              <>
                <FormField label="Subtype">
                  <select
                    value={loc.subtype || ""}
                    disabled={saving || locked}
                    onChange={(e) => updateLocation(index, { subtype: e.target.value })}
                  >
                    <option value="">— Select subtype —</option>
                    {PROJECT_SUMMARY_SUBTYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </FormField>
                <CheckboxMultiSelect
                  label="Service types"
                  options={CONNECTION_OPTIONS}
                  value={loc.connectionTypes}
                  disabled={saving || locked}
                  required
                  onChange={(types) => updateLocation(index, { connectionTypes: types })}
                />
                {locationHasPtpOrDark(loc) ? (
                  <>
                    <FormField label="A location">
                      <textarea
                        rows={2}
                        value={loc.aLocation}
                        disabled={saving || locked}
                        onChange={(e) => updateLocation(index, { aLocation: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Z location">
                      <textarea
                        rows={2}
                        value={loc.zLocation}
                        disabled={saving || locked}
                        onChange={(e) => updateLocation(index, { zLocation: e.target.value })}
                      />
                    </FormField>
                  </>
                ) : null}
                {locationHasDia(loc) ? (
                  <div className="add-project-row">
                    <FormField label="Service speed">
                      <select
                        value={loc.serviceSpeed}
                        disabled={saving || locked}
                        onChange={(e) => updateLocation(index, { serviceSpeed: e.target.value })}
                      >
                        <option value="">— Select speed —</option>
                        {SERVICE_SPEED_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="IPs">
                      <select
                        value={loc.ips}
                        disabled={saving || locked}
                        onChange={(e) => updateLocation(index, { ips: e.target.value })}
                      >
                        <option value="">— Select IPs —</option>
                        {IP_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            /{opt}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                ) : null}
                <div className="add-project-row">
                  <FormField label="MRC">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={loc.mrc}
                      disabled={saving || locked}
                      onChange={(e) => updateLocation(index, { mrc: e.target.value })}
                    />
                  </FormField>
                  <FormField label="NRC">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={loc.nrc}
                      disabled={saving || locked}
                      onChange={(e) => updateLocation(index, { nrc: e.target.value })}
                    />
                  </FormField>
                </div>
              </>
            ) : null}
            <FormField label="Location notes">
              <textarea
                rows={2}
                value={loc.notes}
                disabled={saving || locked}
                onChange={(e) => updateLocation(index, { notes: e.target.value })}
              />
            </FormField>
          </div>
        ))}
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">Linked work orders</h3>
        <p className="project-detail-muted" style={{ margin: "0 0 0.75rem", fontSize: 13 }}>
          Optionally link <em>existing</em> commercial work orders (for imports or multi-site deals).
          Saving never allocates a new work order — use <strong>Close Won</strong> for that.
        </p>
        <WorkOrderLinksPicker
          projects={[
            ...(commercialProjects || []),
            ...((opportunity?.projects || []).map((p) => ({
              id: p.id,
              work_order: p.workOrder,
              name: p.name,
              customer: p.customer,
            })) || []),
          ]}
          selectedIds={form.projectIds}
          disabled={saving}
          onChange={(ids) => setField("projectIds", ids)}
        />
      </section>

      {error ? <p className="crm-error">{error}</p> : null}
      <div className="crm-editor-actions">
        <button type="submit" className="crm-btn crm-btn--primary" disabled={saving}>
          {saving ? "Saving…" : "Save opportunity"}
        </button>
        {!isNew && !locked ? (
          <button
            type="button"
            className="crm-btn crm-btn--success"
            disabled={saving}
            onClick={() => onCloseSale(form)}
          >
            Close Won
          </button>
        ) : null}
        {!isNew && form.status === "open" ? (
          <button type="button" className="crm-btn" disabled={saving} onClick={onMarkLost}>
            Mark lost
          </button>
        ) : null}
        <button type="button" className="crm-btn" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        {!isNew && !locked ? (
          <button type="button" className="crm-btn crm-btn--danger" disabled={saving} onClick={onDelete}>
            Delete
          </button>
        ) : null}
      </div>
    </form>
  );
}

function CrmPageInner({ apiUrl, getAuthHeaders }) {
  const navigate = useNavigate();
  const { tab: tabParam, id: idParam } = useParams();
  const [searchParams] = useSearchParams();
  const tab = CRM_TABS.some((t) => t.id === tabParam) ? tabParam : "opportunities";
  const isNew = idParam === "new";
  const recordId = !isNew && idParam ? Number(idParam) : null;
  const canEdit = canManageCrm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const [people, setPeople] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [company, setCompany] = useState(null);
  const [person, setPerson] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [commercialProjects, setCommercialProjects] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [syncingCompanies, setSyncingCompanies] = useState(false);

  const salesRepOptions = useMemo(() => listMatrixUserEmailsByRoleName("Sales"), []);
  const opts = useMemo(() => ({ apiUrl, getAuthHeaders }), [apiUrl, getAuthHeaders]);

  const loadLists = useCallback(async () => {
    const [c, p, o] = await Promise.all([
      fetchCrmCompanies(opts),
      fetchCrmPeople(opts),
      fetchCrmOpportunities(opts),
    ]);
    setCompanies(c);
    setPeople(p);
    setOpportunities(o);
  }, [opts]);

  const handleSyncCompanies = async () => {
    setSyncingCompanies(true);
    setError("");
    try {
      const result = await syncCrmCompaniesFromProjects(opts);
      await loadLists();
      const count = result?.companyCount ?? result?.companies ?? companies.length;
      setError("");
      window.alert(
        `Synced companies from commercial work orders` +
          (result?.projectRows != null ? ` (${result.projectRows} projects scanned)` : "") +
          `. ${typeof count === "number" ? `${count} companies on file.` : ""}`
      );
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not sync companies.");
    } finally {
      setSyncingCompanies(false);
    }
  };

  const loadCommercialProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/projects`, { headers: getAuthHeaders?.() });
      const rows = Array.isArray(data) ? data : [];
      setCommercialProjects(rows.filter((r) => !r?.cvin_expansion));
    } catch {
      setCommercialProjects([]);
    }
  }, [apiUrl, getAuthHeaders]);

  useEffect(() => {
    loadCommercialProjects();
  }, [loadCommercialProjects]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        await loadLists();
        if (cancelled) return;
        if (tab === "companies" && recordId) {
          setCompany(await fetchCrmCompany(recordId, opts));
        } else {
          setCompany(isNew && tab === "companies" ? {} : null);
        }
        if (tab === "people" && recordId) {
          setPerson(await fetchCrmPerson(recordId, opts));
        } else {
          setPerson(isNew && tab === "people" ? {} : null);
        }
        if (tab === "opportunities" && recordId) {
          setOpportunity(await fetchCrmOpportunity(recordId, opts));
        } else {
          setOpportunity(isNew && tab === "opportunities" ? {} : null);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err.message || "Could not load CRM.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, recordId, isNew, loadLists, opts]);

  const filteredOpportunities = opportunities.filter((row) =>
    matchesQuery(
      `${row.title} ${row.companyName} ${row.status} ${row.salesRep} ${row.workOrder} ${formatOpportunityWorkOrders(row).join(" ")} ${row.serviceSummary}`,
      query
    )
  );
  const filteredCompanies = companies.filter((row) =>
    matchesQuery(`${row.name} ${row.accountingContactName} ${row.notes}`, query)
  );
  const filteredPeople = people.filter((row) =>
    matchesQuery(`${row.name} ${row.companyName} ${row.email} ${row.phone} ${row.role} ${row.title}`, query)
  );

  const handleSaveCompany = async (body) => {
    setSaving(true);
    setError("");
    try {
      const saved = company?.id
        ? await updateCrmCompany(company.id, body, opts)
        : await createCrmCompany(body, opts);
      await loadLists();
      navigate(`/crm/companies/${saved.id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not save company.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePerson = async (body) => {
    setSaving(true);
    setError("");
    try {
      const saved = person?.id
        ? await updateCrmPerson(person.id, body, opts)
        : await createCrmPerson(body, opts);
      await loadLists();
      navigate(`/crm/people/${saved.id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not save person.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOpportunity = async (form) => {
    setSaving(true);
    setError("");
    try {
      const locked = opportunity?.status === "closed";
      const body = locked ? formToClosedOpportunityPayload(form) : formToOpportunityPayload(form);
      const saved = opportunity?.id
        ? await updateCrmOpportunity(opportunity.id, body, opts)
        : await createCrmOpportunity(body, opts);
      await loadLists();
      setOpportunity(saved);
      navigate(`/crm/opportunities/${saved.id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not save opportunity.");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSale = async (form) => {
    const validation = validateOpportunityForClose(form);
    if (validation) {
      setError(validation);
      return;
    }
    if (
      !window.confirm(
        "Mark this opportunity Close Won? A work order will be generated and the project will enter the normal commercial phase process. Email triggers are not sent yet."
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (opportunity?.id) {
        await updateCrmOpportunity(opportunity.id, formToOpportunityPayload(form), opts);
      }
      const result = await closeCrmOpportunity(opportunity.id, opts);
      await loadLists();
      const wo = result?.created?.work_order || result?.opportunity?.workOrder;
      if (result?.created?.multisite && wo) {
        navigate(`/work-order/${wo}`);
        return;
      }
      const projectId = result?.created?.project?.id;
      if (projectId) {
        navigate(`/project/${projectId}`);
        return;
      }
      setOpportunity(result.opportunity);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not close opportunity.");
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <>
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">CRM</h1>
          <p className="crm-page-lead">
            Opportunities pending sale, plus company and people records. Saving an opportunity does
            not create a work order — only <strong>Close Won</strong> generates one and starts the
            commercial phase process.
          </p>
        </div>
      </div>
      <nav className="crm-tabs" aria-label="CRM sections">
        {CRM_TABS.map((t) => (
          <Link
            key={t.id}
            to={`/crm/${t.id}`}
            className={`crm-tab${tab === t.id && !recordId && !isNew ? " is-active" : tab === t.id ? " is-active" : ""}`}
          >
            {t.label}
          </Link>
        ))}
      </nav>
    </>
  );

  if (loading) {
    return (
      <CrmShell>
        {header}
        <p className="crm-muted">Loading CRM…</p>
      </CrmShell>
    );
  }

  if (tab === "companies" && (isNew || recordId)) {
    return (
      <CrmShell>
        {header}
        <Link to="/crm/companies" className="project-detail-back project-landing-back">
          ← All companies
        </Link>
        <CompanyEditor
          company={isNew ? {} : company}
          saving={saving}
          error={error}
          onSave={handleSaveCompany}
          onCancel={() => navigate("/crm/companies")}
          onDelete={async () => {
            if (!company?.id || !window.confirm("Delete this company?")) return;
            try {
              await deleteCrmCompany(company.id, opts);
              navigate("/crm/companies");
            } catch (err) {
              setError(err?.response?.data?.error || err.message || "Could not delete company.");
            }
          }}
        />
        {!isNew && company?.id ? (
          <>
            <section className="crm-panel" style={{ marginTop: 16 }}>
              <div className="crm-toolbar">
                <h3 className="add-project-section-title" style={{ margin: 0 }}>
                  People
                </h3>
                <Link to={`/crm/people/new?companyId=${company.id}`} className="crm-btn">
                  Add person
                </Link>
              </div>
              {(company.people || []).length ? (
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Email</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {company.people.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <Link to={`/crm/people/${p.id}`}>{p.name}</Link>
                        </td>
                        <td>{personRoleLabel(p.role)}</td>
                        <td>{p.email || "—"}</td>
                        <td>{p.phone || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="crm-muted">No people on this company yet.</p>
              )}
            </section>
            <section className="crm-panel">
              <div className="crm-toolbar">
                <h3 className="add-project-section-title" style={{ margin: 0 }}>
                  Opportunities
                </h3>
                <Link to={`/crm/opportunities/new?companyId=${company.id}`} className="crm-btn">
                  Add opportunity
                </Link>
              </div>
              {(company.opportunities || []).length ? (
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Opportunity</th>
                      <th>Status</th>
                      <th>Work order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {company.opportunities.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <Link to={`/crm/opportunities/${o.id}`}>{o.title || "Untitled"}</Link>
                        </td>
                        <td>
                          <span className={`crm-status ${opportunityStatusClass(o.status)}`}>
                            {opportunityStatusLabel(o.status)}
                          </span>
                        </td>
                        <td>
                          {formatOpportunityWorkOrders(o).join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="crm-muted">No opportunities for this company yet.</p>
              )}
            </section>
          </>
        ) : null}
      </CrmShell>
    );
  }

  if (tab === "people" && (isNew || recordId)) {
    return (
      <CrmShell>
        {header}
        <Link to="/crm/people" className="project-detail-back project-landing-back">
          ← All people
        </Link>
        <PersonEditor
          person={isNew ? {} : person}
          companies={companies}
          initialCompanyId={searchParams.get("companyId")}
          saving={saving}
          error={error}
          onSave={handleSavePerson}
          onCancel={() => navigate("/crm/people")}
          onDelete={async () => {
            if (!person?.id || !window.confirm("Delete this person?")) return;
            try {
              await deleteCrmPerson(person.id, opts);
              navigate("/crm/people");
            } catch (err) {
              setError(err?.response?.data?.error || err.message || "Could not delete person.");
            }
          }}
        />
      </CrmShell>
    );
  }

  if (tab === "opportunities" && (isNew || recordId)) {
    return (
      <CrmShell>
        {header}
        <Link to="/crm/opportunities" className="project-detail-back project-landing-back">
          ← All opportunities
        </Link>
        <OpportunityEditor
          opportunity={isNew ? null : opportunity}
          companies={companies}
          people={people}
          commercialProjects={commercialProjects}
          salesRepOptions={salesRepOptions}
          initialCompanyId={searchParams.get("companyId")}
          saving={saving}
          error={error}
          onSave={handleSaveOpportunity}
          onCancel={() => navigate("/crm/opportunities")}
          onCloseSale={handleCloseSale}
          onMarkLost={async () => {
            if (!opportunity?.id) return;
            setSaving(true);
            setError("");
            try {
              const saved = await updateCrmOpportunity(opportunity.id, { status: "lost" }, opts);
              setOpportunity(saved);
              await loadLists();
            } catch (err) {
              setError(err?.response?.data?.error || err.message || "Could not update opportunity.");
            } finally {
              setSaving(false);
            }
          }}
          onDelete={async () => {
            if (!opportunity?.id || !window.confirm("Delete this opportunity?")) return;
            try {
              await deleteCrmOpportunity(opportunity.id, opts);
              navigate("/crm/opportunities");
            } catch (err) {
              setError(err?.response?.data?.error || err.message || "Could not delete opportunity.");
            }
          }}
        />
      </CrmShell>
    );
  }

  return (
    <CrmShell>
      {header}
      {error ? <p className="crm-error">{error}</p> : null}

      {tab === "opportunities" ? (
        <section className="crm-panel">
          <div className="crm-toolbar">
            <input
              className="crm-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search opportunities…"
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {canEdit ? (
                <button
                  type="button"
                  className="crm-btn"
                  onClick={() => setShowImport((v) => !v)}
                >
                  {showImport ? "Hide import" : "Import CSV"}
                </button>
              ) : null}
              {canEdit ? (
                <Link to="/crm/opportunities/new" className="crm-btn crm-btn--primary">
                  New opportunity
                </Link>
              ) : null}
            </div>
          </div>
          {showImport && canEdit ? (
            <div style={{ marginBottom: "1.25rem" }}>
              <CrmCsvImportPanel
                compact
                onImported={() => {
                  loadLists();
                }}
              />
            </div>
          ) : null}
          {filteredOpportunities.length ? (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Services</th>
                  <th>Locations</th>
                  <th>Work orders</th>
                </tr>
              </thead>
              <tbody>
                {filteredOpportunities.map((row) => {
                  const wos = formatOpportunityWorkOrders(row);
                  return (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/crm/opportunities/${row.id}`}>{row.title || "Untitled"}</Link>
                    </td>
                    <td>
                      <Link to={`/crm/companies/${row.companyId}`}>{row.companyName}</Link>
                    </td>
                    <td>
                      <span className={`crm-status ${opportunityStatusClass(row.status)}`}>
                        {opportunityStatusLabel(row.status)}
                      </span>
                    </td>
                    <td>{row.serviceSummary || "—"}</td>
                    <td>{row.locationCount ?? "—"}</td>
                    <td>
                      {wos.length ? (
                        wos.map((wo, i) => {
                          const project =
                            (row.projects || []).find((p) => p.workOrder === wo) ||
                            (row.projectId && row.workOrder === wo ? { id: row.projectId } : null);
                          return (
                            <React.Fragment key={wo}>
                              {i > 0 ? ", " : null}
                              {project?.id ? (
                                <Link to={`/project/${project.id}`}>{wo}</Link>
                              ) : (
                                <Link to={`/work-order/${wo}`}>{wo}</Link>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="crm-muted">No opportunities yet. Create one to track a pending sale.</p>
          )}
        </section>
      ) : null}

      {tab === "companies" ? (
        <section className="crm-panel">
          <div className="crm-toolbar">
            <input
              className="crm-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search companies…"
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {canEdit ? (
                <button
                  type="button"
                  className="crm-btn"
                  disabled={syncingCompanies}
                  onClick={handleSyncCompanies}
                >
                  {syncingCompanies ? "Syncing…" : "Sync from work orders"}
                </button>
              ) : null}
              {canEdit ? (
                <Link to="/crm/companies/new" className="crm-btn crm-btn--primary">
                  New company
                </Link>
              ) : null}
            </div>
          </div>
          {filteredCompanies.length ? (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Accounting contact</th>
                  <th>People</th>
                  <th>Opportunities</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/crm/companies/${row.id}`}>{row.name}</Link>
                    </td>
                    <td>
                      {[row.accountingContactName, row.accountingContactEmail]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </td>
                    <td>{row.peopleCount ?? "—"}</td>
                    <td>{row.opportunityCount ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="crm-muted">
              No companies yet. Use <strong>Sync from work orders</strong> to pull customers from all
              commercial projects (including completed and archived).
            </p>
          )}
        </section>
      ) : null}

      {tab === "people" ? (
        <section className="crm-panel">
          <div className="crm-toolbar">
            <input
              className="crm-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people…"
            />
            {canEdit ? (
              <Link to="/crm/people/new" className="crm-btn crm-btn--primary">
                New person
              </Link>
            ) : null}
          </div>
          {filteredPeople.length ? (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Phone</th>
                </tr>
              </thead>
              <tbody>
                {filteredPeople.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/crm/people/${row.id}`}>{row.name}</Link>
                    </td>
                    <td>
                      {row.companyId ? (
                        <Link to={`/crm/companies/${row.companyId}`}>{row.companyName || "—"}</Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{personRoleLabel(row.role)}</td>
                    <td>{row.email || "—"}</td>
                    <td>{row.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="crm-muted">No people yet. Site, IT, and accounting contacts can be added here.</p>
          )}
        </section>
      ) : null}
    </CrmShell>
  );
}

export function CrmGate({ apiUrl, getAuthHeaders }) {
  if (!isPluginEnabled(PLUGIN_IDS.CRM)) {
    return <Navigate to="/" replace />;
  }
  if (!canViewCrm()) {
    return <Navigate to="/" replace />;
  }
  return <CrmPageInner apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}

export default CrmGate;
